import { faMicrosoft, faSlack } from '@fortawesome/free-brands-svg-icons';

import { MsTeamsTargetingClient, SlackTargetingClient } from 'jf/api';
import { notNull } from 'jf/utils/notNull';
import { useClientFlags } from 'jf/utils/useClientFlags';
import { useClientQuery } from 'jf/utils/useClientQuery';

export const useTargetingClient = () => {
  const { msTeamsTargeting } = useClientFlags();

  const msgProvider = msTeamsTargeting ? 'MS Teams' : 'Slack';
  const msgProviderFull = msTeamsTargeting ? 'Microsoft Teams' : 'Slack';
  const msgProviderLower = msTeamsTargeting ? 'msteams' : 'slack';
  const msgIcon = msTeamsTargeting ? faMicrosoft : faSlack;

  const useSyncUsers = ({
    isEnabled,
    studyRef,
    cursor,
    pageSize,
  }: {
    isEnabled: boolean;
    studyRef?: string;
    cursor?: string;
    pageSize?: number;
  }) => {
    const { data: msTeamsUsers, isFetching: msTeamsIsFetching } = useClientQuery(
      MsTeamsTargetingClient.syncMsteamsUsers,
      { ...(cursor && { cursor }), ...(pageSize && { pageSize }) },
      { enabled: isEnabled && msTeamsTargeting }
    );

    const { data: slackUsers, isFetching: slackIsFetching } = useClientQuery(
      SlackTargetingClient.syncSlackUsers,
      { studyRef, ...(cursor && { cursor }), ...(pageSize && { pageSize }) },
      { enabled: isEnabled && notNull(msTeamsTargeting) && !msTeamsTargeting }
    );

    return msTeamsTargeting
      ? { data: msTeamsUsers, isFetching: msTeamsIsFetching }
      : { data: slackUsers, isFetching: slackIsFetching };
  };

  const useGetStatus = () => {
    // Do not initiate a slack status check on views shared using a scoped token
    const scopeToken = new URLSearchParams(window.location.search).get('scope_token');

    const getMsteamsStatusQuery = useClientQuery(MsTeamsTargetingClient.getMsteamsStatus, null, {
      enabled: msTeamsTargeting && !scopeToken,
    });

    const getSlackStatusQuery = useClientQuery(SlackTargetingClient.getSlackStatus, null, {
      enabled: notNull(msTeamsTargeting) && !msTeamsTargeting && !scopeToken,
    });

    return msTeamsTargeting ? getMsteamsStatusQuery : getSlackStatusQuery;
  };

  type MaybeString = string | undefined;
  type MaybeBoolean = boolean | undefined;

  const useGetStudyTargets = ({
    studyRef,
    isEnabled,
  }: {
    studyRef: MaybeString;
    isEnabled: MaybeBoolean;
  }) => {
    const getStudyMsteamsTargetsQuery = useClientQuery(
      MsTeamsTargetingClient.getStudyMsteamsTargets,
      { studyRef },
      {
        enabled: !!isEnabled && msTeamsTargeting,
      }
    );

    const getStudySlackTargetsQuery = useClientQuery(
      SlackTargetingClient.getStudySlackTargets,
      { studyRef },
      {
        enabled: !!isEnabled && notNull(msTeamsTargeting) && !msTeamsTargeting,
      }
    );

    return msTeamsTargeting ? getStudyMsteamsTargetsQuery : getStudySlackTargetsQuery;
  };

  return {
    useSyncUsers,
    useGetStatus,
    useGetStudyTargets,
    msgProvider,
    msgProviderLower,
    msgProviderFull,
    msgIcon,
  };
};
